<template>
  <el-drawer
    v-model="showProxy"
    class="modal-container"
    direction="rtl"
    size="80%"
    :with-header="true"
    :append-to-body="true"
    :modal-append-to-body="true"
    @close="handleClose()"
  >
    <!-- >>>>>> 弹窗头部 <<<<<< -->
    <template #header>
      <div class="header-container">
        <div class="icon-container"></div>
        <span class="title-text">{{ $str.getKey('收益管理/新增电表时段') }}</span>
      </div>
    </template>

    <!-- >>>>>> 弹窗内容 <<<<<< -->
    <template #default>
      <div class="content-container" v-loading="loading">
        <!-- 表单选项：时间|地区 -->
        <div class="top-form-container">
          <el-form inline :model="formData">
            <el-form-item :label="$str.getKey('收益管理/年份')">
              <el-date-picker v-model="formData.year" type="year" :placeholder="$str.getKey('收益管理/请选择年份')" :clearable="false" :editable="false"></el-date-picker>
            </el-form-item>
            <el-form-item :label="$str.getKey('收益管理/省市')">
              <el-cascader v-model="formData.adCode"  :options="adOptions" @change="handleAdChange" :props="cascaderProps" :show-all-levels="false" clearable />
            </el-form-item>
          </el-form>
        </div>
        <div class="main-panel-container">
          <el-scrollbar class="scroll-container">
            <!-- 配置规则 -->
            <RulesPanel :year="formData.year" :adCode="formData.adCode" @ruleListChange="handleRuleListChange"></RulesPanel>
            <!-- 选择生效月份 -->
            <CfgCalendarPanel :year="formData.year" :ruleList="formData.ruleList" @dateRuleChange="handleDateRuleChange"></CfgCalendarPanel>
            <!-- 已选日期 -->
            <CalendarPanel :year="formData.year" :ruleList="formData.ruleList"></CalendarPanel>
          </el-scrollbar>
        </div>
      </div>
    </template>

    <!-- >>>>>> 弹窗底部 <<<<<< -->
    <template #footer>
      <div class="footer-container">
        <el-button class="meter-feature-btn" @click="handleClose()" :disabled="isConfirming">{{ $str.getKey('收益管理/取消') }}</el-button>
        <el-button type="primary" @click="onConfirm" class="meter-feature-btn" :disabled="isConfirming" v-loading="loading">{{ $str.getKey('收益管理/确定') }}</el-button>
      </div>
    </template>
  </el-drawer>
</template>
<script setup>
import { defineEmits, defineOptions, defineProps, reactive, ref,  computed, provide, watch, inject } from "vue";
import { useVModel } from "@vueuse/core";
import { ElMessageBox } from 'element-plus'
import dayjs from 'dayjs';
import { useAppStore } from "@/store/modules/app";
import { queryAdRegionInfo, commitCreateData } from "../api/index.js";
import { cloneDeep } from "@/utils"
import RulesPanel from "./rulesPanel.vue"
import CfgCalendarPanel from "./cfgCalendarPanel.vue"
import CalendarPanel from "./calendarPanel.vue"
import { defaultColorList } from "@/utils/colorHelper/index.js";
import { isWeekEnd, checkDateIsInDateRange } from "@/utils/timeHelper/index.js";

const $str = window.$str;

const trackEvent = inject('trackEvent'); 
const trackEventTypes = inject('trackEventTypes');
const trackDatas = reactive({
  commitCreate: {
    targetId: '',
    refData: {
      targetName: '电表设置/新增电表时段/确定按钮',
      desc: '执行信息提交'
    },
  },
})


provide('getRuleColorByDate', getRuleColorByDate);
provide('checkRuleIsUse', checkRuleIsUse);


/** 设置组件配置项 */
defineOptions({
  name: "CreateModal"
});

const stationId = computed(() => {
  return useAppStore().stationId
})

/** 接收父组件信息 */
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    require: '',
  },
});

const emit = defineEmits(['update:show', 'onClose']);
const showProxy = useVModel(props, "show", emit);
const loading = ref(false); // 加载中
const isConfirming = ref(false); // 防抖【连续多次点击提交按钮】


const origin = {
  cfgInfo: {
    common: [],
    dayoff: [],
    holiday: [],
    loanedDay: [],
  },
}

const adOptions = ref([]); // 存储行政区域的下拉选项
const cascaderProps = { // 级联配置对象
  checkStrictly: true,
  expandTrigger: 'hover',
  emitPath: false, // 只返回当前节点的值
};

// 表单信息
const formData = reactive({
  year: dayjs().format('YYYY'),
  adCode: '', // 示例：山东省-370000
  ruleList: [],
  cfgInfo: cloneDeep(origin.cfgInfo),
})

// 初始化面板
initPanel();






/** ====== [业务：功能方法] =================================================================== */
/** 初始化：面板信息 */
function initPanel() {
  getAdRegionOptions();
}



/** 关闭面板 */
function handleClose(needRefresh = false) {
  // 通知父组件关闭当前弹窗。 参数: 父组件是否需要刷新数据。 
  emit("onClose", props.type, needRefresh);
}


/** 提交表单 */
async function onConfirm() {
  // 格式化提交接口需要的参数
  const params = {
    year: dayjs(formData.year).format('YYYY'),
  }

  // 数据校验
  let isOK = true;

  // 校验：选择省市
  isOK = checkAdCode(formData.adCode)
  if (isOK) {
    params.provinceCode = formData.adCode;
    params.province = getAdNameByCode(formData.adCode, cloneDeep(adOptions.value));
  } else {
    return false;
  }

  isOK = checkCalendarData(formData.cfgInfo);
  if (isOK) {
    completeCreateParams(params);
  } else {
    return false;
  }

  // 提交创建
  loading.value = true;
  isConfirming.value = true;
  commitCreateData(params).then(res => {
    // 埋点信息：
    trackDatas.commitCreate.refData['year'] = dayjs(formData.year).format('YYYY');
    trackDatas.commitCreate.refData['adCode'] = formData.adCode;
    trackEvent(trackEventTypes.create, trackDatas.commitCreate);

    // 操作成功，通知用户
    window.dispatchNotify({
      type: "success",
      message: $str.getKey('收益管理/创建成功！')
    });

    handleClose(); // 关闭弹窗-通知父组件刷新数据
  }).catch(err => {
    console.error('新增电表时段，创建接口异常', err);
  }).finally(() => {
    loading.value = false;
    isConfirming.value = false;
  })
}


/** 事件响应：行政区域发生变化 */
function handleAdChange(adCode) {
  formData.adCode = adCode;
}


/** 事件响应：规则列表发生变化 */
function handleRuleListChange(ruleList) {
  formData.ruleList = addRuleColor(ruleList);
}


/** 事件响应：日期的规则发生变化 */
function handleDateRuleChange(data) {
  // 向【已选日期】组件，传递：规则数组，已有规则的日期数组
  formData.cfgInfo.common = data.common;
  formData.cfgInfo.dayoff = data.dayoff;
  formData.cfgInfo.holiday = data.holiday;
  formData.cfgInfo.loanedDay = data.loanedDay;
}


/** 校验：ruleId对应的规则是否正在使用中 */
function checkRuleIsUse(ruleId) {
  const list = [...formData.cfgInfo.common, ...formData.cfgInfo.common, ...formData.cfgInfo.common, ...formData.cfgInfo.common];
  return list.some(it => it.ruleId === ruleId);
}




/** ====== [辅助：功能方法] =================================================================== */

/** 获取行政区域的下拉选项 */
function getAdRegionOptions() {
  queryAdRegionInfo().then(res => {
    adOptions.value = formatCascaderOption(res?.data);
  })
}


/** 格式化行政区域下拉选项的数据结构 */
function formatCascaderOption(list) {
  if (Array.isArray(list)) {
    return list.map(item => {
      const newItem = {
        value: item.code,
        label: item.name,
        children: [],
      }
      if (Array.isArray(item.cityList)) {
        newItem.children = formatCascaderOption(item.cityList);
      }
      return newItem;
    })
  }
  console.error('接口返回的行政区域选项应为数组！')
  return [];
}


/** 补充信息：给规则添加颜色 */
function addRuleColor(list) {
  const ruleList = cloneDeep(list)
  const len = defaultColorList.length;
  ruleList.forEach((rule, idx) => {
    rule['color'] = defaultColorList[idx%len];
  })
  return ruleList;
}


/** 获取：日期使用规则对应的颜色 */
function getRuleColorByDate(date, ruleList = formData.ruleList) {
  // ------ 法定节假日 ------
  let ruleId = findRuleIdFromHoliday(date);
  if (ruleId) {
    const rule = ruleList.find(it => it.id === ruleId);
    return rule?.color ?? '';
  }


  // ------ 法定调班日：返回【当月普通工作日使用规则】的颜色 ------
  // 如果是调班，当作正常工作日对待。使用当月的过昨日设定的规则。
  ruleId = findRuleIdFromLoanedDay(date);
  if (ruleId) {
    const rule = ruleList.find(it => it.id === ruleId);
    return rule?.color ?? '';
  }


  // ------ 周六日：返回【当月休息日使用规则】的颜色 ------
  ruleId = findRuleIdFromDayoff(date);
  if (ruleId) {
    const rule = ruleList.find(it => it.id === ruleId);
    return rule?.color ?? '';
  }

  // ------ 常规默认：返回【当普通工作日使用规则】的颜色 ------
  ruleId = findRuleIdFromCommon(date);
  if (ruleId) {
    const rule = ruleList.find(it => it.id === ruleId);
    return rule?.color ?? '';
  }

  return '';
}


/** 查找：在 holiday 中查找 date 对应的 ruleId */
function findRuleIdFromHoliday(date, calendarInfo = formData.cfgInfo) {
  const holiday = calendarInfo.holiday.find(it => {
    return checkDateIsInDateRange(date, it.dateRange);
  });
  if (holiday) {
    return holiday.ruleId;
  } else {
    return undefined;
  }
}



/** 查找：在 loanedDay 中查找 date 对应的 ruleId */
function findRuleIdFromLoanedDay(date, calendarInfo = formData.cfgInfo) {
  const loanedDay = calendarInfo.loanedDay.find(it => {
    return checkDateIsInDateRange(date, it.dateRange);
  });
  if (loanedDay) {
    return findRuleIdFromCommon(date);
  } else {
    return undefined;
  }
}


/** 查找：在 dayoff 中查找 date 对应的 ruleId */
function findRuleIdFromDayoff(date, calendarInfo = formData.cfgInfo) {
  if (!isWeekEnd(date)) {
    return undefined;
  }
  const month = calendarInfo.dayoff.find(it => {
    return +it.month === (+dayjs(date).get('month') + 1);
  });
  if (month) {
    return month.ruleId;
  } else {
    return undefined;
  }
}


/** 查找：在 common 中查找 date 对应的 ruleId */
function findRuleIdFromCommon(date, calendarInfo = formData.cfgInfo) {
  const month = calendarInfo.common.find(it => {
    return +it.month === (+dayjs(date).get('month') + 1);
  });
  if (month) {
    return month.ruleId;
  } else {
    return undefined;
  }
}


/** 检查：是否选择了行政区域编码 */
function checkAdCode(adCode) {
  if (adCode) {
    return true;
  } else {
    window.dispatchNotify({
      type: "warning",
      message: $str.getKey('收益管理/请选择省市区域！')
    });
    return false;
  }
}


/** 检查：是否选择了行政区域编码 */
function checkCalendarData(calendarData) {
  const isOk = Array.isArray(calendarData.common) && (calendarData.common.length === 12) && calendarData.common.every(it => it.ruleId);
  if (isOk) {
    return true;
  } else {
    window.dispatchNotify({
      type: "warning",
      message: $str.getKey('收益管理/规则配置应该覆盖一整年！')
    });
    return false;
  }
}


/** 获取：地区名称 */
function getAdNameByCode(adCode, adList, searchLevel = 0) {
  const len = adList.length;
  for (let i = 0; i < len; i++) {
    const tmpAdCode = adList[i].value;
    const sIdx = searchLevel * 2;
    const currLevelCode = adCode.slice(sIdx, 2);
    const tmpLevelCode = tmpAdCode.slice(sIdx, 2);
    if (currLevelCode === tmpLevelCode) {
      if (tmpAdCode === adCode) {
        return adList[i].label;
      } else {
        const childrenList = cloneDeep(adList[i].children);
        const adName = getAdNameByCode(adCode, childrenList, searchLevel + 1);
        if (adName) {
          return adName;
        }
      }
    }
  }
  return '';
}


/** 完善参数：创建接口需要的 ruleList & calendarData 参数 */
function completeCreateParams(params) {
  const originFormData = cloneDeep(formData);
  params.ruleList = formatRuleListToCreate(originFormData);
  params.calendarData = formatCalendarDataToCreate(originFormData, params.ruleList);
}



/** 格式化：获取创建接口参数中需要的 ruleList 数据 */
function formatRuleListToCreate(originData) {
  const ruleList = [];

  // 过滤出有效的规则列表
  const common = originData.cfgInfo?.common ?? [];
  const dayoff = originData.cfgInfo?.dayoff ?? [];
  const loanedDay = originData.cfgInfo?.loanedDay ?? [];
  const holiday = originData.cfgInfo?.holiday ?? [];
  const allList = [...common, ...dayoff, ...loanedDay, ...holiday];
  const validRuleIdList = originData.ruleList.filter(it => {
    return allList.some(d => d.ruleId === it.id);
  });

  // 设置type，注意调用接口前删除ruleId字段
  // >>> eLink的人员非要将id转换成：从1开始排序的type值，来获取规则信息。而且还不想自己转！！！
  validRuleIdList.forEach((it, idx) => {
    const rule = {
      type: idx + 1, // 下发给eLink的规则类型，需要从1开始数字编码
      // name: it.name,
      name: `规则${idx + 1}`, // 重新格式化命名：规则1、规则2、...
      ruleInfo: it.segments,
      id: it.id, // 用于给 calendarData 获取规则对应的 type。
    }
    ruleList.push(rule);
  })

  return ruleList;
}



/** 格式化：获取创建接口参数中需要的 calendarData 数据 */
function formatCalendarDataToCreate(originData, ruleList) {
  const calendarData = [];
  let currDay = dayjs(originData.year).startOf('day');
  const endDay = dayjs(originData.year).add(1, 'year').startOf('day');

  while(currDay.isBefore(endDay)) {
    const ruleId = getRuleIdByDate(currDay, originData);
    const rule = ruleList.find(it => it.id === ruleId);

    const dayInfo = {
      date: currDay.format('YYYY-MM-DD'),
      ruleType: rule.type,
    }
    calendarData.push(dayInfo);

    currDay = currDay.add(1, 'day');
  }

  // 删除 ruleList参数 中的 id 属性
  ruleList.forEach(it => {
    Reflect.deleteProperty(it, 'id');
  })

  return calendarData;
}


/** 获取：从日历配置对象中，获取date对应的ruleId */
function getRuleIdByDate(date, originData) {
  let ruleId = findRuleIdFromHoliday(date, originData.cfgInfo);
  if (ruleId) {
    return ruleId;
  }

  ruleId = findRuleIdFromLoanedDay(date, originData.cfgInfo);
  if (ruleId) {
    return ruleId;
  }

  ruleId = findRuleIdFromDayoff(date, originData.cfgInfo);
  if (ruleId) {
    return ruleId;
  }

  ruleId = findRuleIdFromCommon(date, originData.cfgInfo);
  if (ruleId) {
    return ruleId;
  }

  return '';
}




</script>

<style lang="scss" scoped>
@import '../button-style.scss';

.modal-container {
  & {
    height: 100%;
    overflow: hidden;
  }

  :deep(.el-drawer__header) {
    margin-bottom: 0;
    padding: 14px 16px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  :deep(.el-drawer__body) {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(10px);
  }

  :deep(.el-drawer__footer) {
    padding: 12px 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .header-container {
    & {
      height: auto;
      padding-left: 0;
      display: flex;
      align-items: center;
    }

    .icon-container {
      display: none;
    }

    .title-text {
      font-family: PingFang-SC, PingFang-SC;
      font-size: 16px;
      font-weight: 600;
      color: rgba(46, 46, 46, 0.88);
      text-align: left;
      letter-spacing: 0;
    }
  }

  .content-container {
    & {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      padding: 14px 16px 16px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .top-form-container {
      // background-color: lime;
      width: 100%;
      height: 50px;
      min-height: 50px;
      display: flex;
      align-items: center;

      .el-form-item {
        margin-bottom: 0;
      }

    }
    
    .main-panel-container {
      height: 100%;
      flex: 1;
      overflow: hidden;
      .scroll-container {
        height: 100%;
      }
    }

  }

  .footer-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
  }

}
</style>